import { DOCUMENT } from '@angular/common';
import { HttpContext } from '@angular/common/http';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { filter, fromEvent, interval } from 'rxjs';
import { environment } from '../../../environments/environment';
import { KeycloakService } from '../auth/keycloak.service';
import { SKIP_ERROR_NOTIFY } from '../interceptors/error.interceptor';
import { SKIP_AUTH_REDIRECT } from '../interceptors/auth.interceptor';
import { SKIP_LOADING } from '../interceptors/loading.interceptor';
import { ApiService } from '../services/api.service';
import { ActivityBatch, ActivityConfig, ActivityEvent, ActivityEventName, ClientProfile } from './activity.model';
import { collectClientProfile } from './client-profile';
import { routeInfo } from './route-template';

const MS_PER_SECOND = 1000;

/** A request the user never sees: no spinner, no error snackbar, no access-denied popup. */
const QUIET = new HttpContext().set(SKIP_LOADING, true).set(SKIP_ERROR_NOTIFY, true).set(SKIP_AUTH_REDIRECT, true);

/** Optional fields of an event; the name, time and tab are filled in by the tracker. */
export type ActivityFields = Partial<Pick<ActivityEvent, 'module_cd' | 'route_template' | 'action_cd' | 'outcome' | 'duration_ms' | 'meta'>>;

function newId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Records what a signed-in user does — pages visited, named actions, errors, a heartbeat while the tab
 * is in the foreground — and ships it to the server in small batches.
 *
 * Nothing here can slow the portal: events are queued in memory and sent every few seconds in the
 * background, with no spinner and no error popup; a full queue drops the oldest events; a failed send
 * is retried once later and then given up. The server never trusts this data for anything but
 * analytics: it takes the user, node and session from the verified token.
 *
 * Closing: `pagehide` reports `session.tab_closed` (best effort: a crash or power loss sends nothing,
 * so the server still infers inactivity from missing heartbeats).
 */
@Injectable({ providedIn: 'root' })
export class ActivityTrackerService {
  private readonly router = inject(Router);
  private readonly api = inject(ApiService);
  private readonly keycloak = inject(KeycloakService);
  private readonly doc = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  private readonly config: ActivityConfig = environment.activity;
  private readonly tabId = newId();
  private queue: ActivityEvent[] = [];
  /** Events already re-queued once after a failed send; a second failure drops them. */
  private readonly retried = new WeakSet<ActivityEvent>();
  private started = false;
  private profileSent = false;
  private sending = false;
  private sessionAnnounced = false;

  /** The page currently shown, and the foreground milliseconds it has accumulated. */
  private page = { template: '', module: '' };
  private pageForegroundMs = 0;
  private visibleSince: number | null = null;

  /** Monotonic clock; replaceable in tests. */
  now: () => number = () => performance.now();

  /** Starts listening. Safe to call more than once. */
  start(): void {
    if (this.started || !this.config.enabled) return;
    this.started = true;
    this.visibleSince = this.isVisible() ? this.now() : null;

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(e => {
      if (e instanceof NavigationEnd) this.onPageChanged();
      else if (e instanceof NavigationError) this.track(ActivityEventName.navigationFailed, { route_template: this.safeRoute(e.url), outcome: 'failed' });
    });
    fromEvent(this.doc, 'visibilitychange').pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onVisibilityChanged());
    const view = this.doc.defaultView;
    if (typeof view?.addEventListener === 'function') {
      fromEvent<PageTransitionEvent>(view, 'pagehide').pipe(takeUntilDestroyed(this.destroyRef)).subscribe(e => this.onTabClosed(e.persisted));
    }
    interval(this.config.heartbeatSeconds * MS_PER_SECOND)
      .pipe(filter(() => this.isVisible()), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.track(ActivityEventName.sessionHeartbeat, { route_template: this.page.template, module_cd: this.page.module }));
    interval(this.config.flushSeconds * MS_PER_SECOND).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.flush());
    this.keycloak.onBeforeLogout(() => this.endSession('logout'));
  }

  /** Queues an event. Ignored when tracking is off or nobody is signed in. */
  track(name: string, fields: ActivityFields = {}): void {
    if (!this.config.enabled || !this.keycloak.isLoggedIn()) return;
    this.queue.push({ event_name: name, client_at: new Date().toISOString(), tab_id: this.tabId, ...fields });
    if (this.queue.length > this.config.maxQueue) this.queue.splice(0, this.queue.length - this.config.maxQueue);
    if (this.queue.length >= this.config.flushMaxEvents) this.flush();
  }

  /** A named business action, e.g. `voucher.save.clicked`; never the content of any form. */
  trackAction(actionCd: string, outcome = 'clicked', meta?: Record<string, string>): void {
    this.track(ActivityEventName.actionClicked, { action_cd: actionCd, outcome, module_cd: this.page.module, route_template: this.page.template, meta });
  }

  /** A browser exception: its class and a short message, never the stack. */
  trackError(error: unknown): void {
    const e = error as { name?: string; constructor?: { name?: string }; message?: unknown } | null;
    const type = String(e?.name || e?.constructor?.name || 'Error').slice(0, 80);
    const message = String(e?.message ?? '').replace(/\s+/g, ' ').slice(0, this.config.maxMessageLength);
    this.track(ActivityEventName.frontendException, { route_template: this.page.template, module_cd: this.page.module, outcome: 'error', meta: { error_type: type, message } });
  }

  /** Confirmed end of the session (logout): sent immediately, surviving the page unload. */
  endSession(reason: string): void {
    this.track(ActivityEventName.sessionEnded, { meta: { reason }, route_template: this.page.template });
    this.flushWhenLeaving();
  }

  /** Sends what is queued, off the request path of everything else. */
  flush(): void {
    if (this.sending || this.queue.length === 0 || !this.keycloak.isLoggedIn()) return;
    const events = this.queue.splice(0, this.config.flushMaxEvents * 2);
    const body = this.buildBatch(events);
    this.sending = true;
    this.api.post<unknown>(this.config.endpoint, body, { context: QUIET }).subscribe({
      next: () => {
        this.sending = false;
        if (body.profile) this.profileSent = true;
      },
      error: (err: { status?: number }) => {
        this.sending = false;
        // Retry later unless the server told us to stop (4xx): bad data will not become good.
        if (err?.status && err.status < 500) return;
        const requeue = events.slice(-this.config.flushMaxEvents).filter(e => !this.retried.has(e));
        requeue.forEach(e => this.retried.add(e));
        this.queue.unshift(...requeue);
      }
    });
  }

  /** Last-chance send for a page that is hiding or a user who is leaving; fetch+keepalive outlives the page. */
  private flushWhenLeaving(): void {
    const token = this.keycloak.getToken();
    if (this.queue.length === 0 || !token) return;
    const body = this.buildBatch(this.queue.splice(0));
    if (body.profile) this.profileSent = true;
    fetch(`${environment.apiBaseUrl}/${this.config.endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
      keepalive: true
    }).catch(() => { /* best effort */ });
  }

  private buildBatch(events: ActivityEvent[]): ActivityBatch {
    const batch: ActivityBatch = { events };
    if (!this.profileSent) {
      const view = this.doc.defaultView;
      if (view) batch.profile = this.profile(view);
    }
    return batch;
  }

  private profile(view: Window): ClientProfile {
    return collectClientProfile({ win: view, nav: view.navigator, doc: this.doc }, this.config.appVersion);
  }

  private onPageChanged(): void {
    const next = routeInfo(this.router.routerState.snapshot.root);
    if (this.sessionAnnounced && next.template === this.page.template) return;
    if (!this.sessionAnnounced) {
      this.sessionAnnounced = true;
      this.track(ActivityEventName.appOpened, { route_template: next.template, module_cd: next.module });
      this.track(ActivityEventName.sessionStarted, { route_template: next.template, module_cd: next.module });
    } else if (this.page.template) {
      this.track(ActivityEventName.pageLeft, { route_template: this.page.template, module_cd: this.page.module, duration_ms: Math.round(this.takeForegroundMs()) });
    }
    this.page = next;
    this.pageForegroundMs = 0;
    this.visibleSince = this.isVisible() ? this.now() : null;
    this.track(ActivityEventName.pageViewed, { route_template: next.template, module_cd: next.module });
  }

  /**
   * The page is going away (tab or window closed, or a hard navigation away). `persisted` is true when
   * the browser keeps the page in its back/forward cache, so it may come back: that is not a close.
   */
  private onTabClosed(persisted: boolean): void {
    this.track(ActivityEventName.sessionTabClosed, {
      route_template: this.page.template,
      module_cd: this.page.module,
      duration_ms: Math.round(this.takeForegroundMs()),
      meta: { persisted: String(persisted) }
    });
    this.flushWhenLeaving();
  }

  private onVisibilityChanged(): void {
    if (this.isVisible()) {
      this.visibleSince = this.now();
      this.track(ActivityEventName.pageVisible, { route_template: this.page.template, module_cd: this.page.module });
      return;
    }
    this.track(ActivityEventName.pageHidden, { route_template: this.page.template, module_cd: this.page.module, duration_ms: Math.round(this.takeForegroundMs()) });
    this.flushWhenLeaving();
  }

  /** Foreground time on the current page so far; time while the tab was hidden does not count. */
  private takeForegroundMs(): number {
    if (this.visibleSince !== null) {
      this.pageForegroundMs += this.now() - this.visibleSince;
      this.visibleSince = null;
    }
    return this.pageForegroundMs;
  }

  private isVisible(): boolean {
    return this.doc.visibilityState !== 'hidden';
  }

  private safeRoute(url: string): string {
    return url.split(/[?#]/)[0];
  }
}
